import type { FilePublicWithPermissions } from '@repo/types/files';
import type { InputHTMLAttributes } from 'react';

import { DirectoryService } from '@repo/api/directory/directoryService';
import { FileService } from '@repo/api/file/fileService';
import { useGetGroup } from '@repo/api/group/useGetGroup';
import { useGetGroupDirectoryWithShared } from '@repo/api/group/useGetGroupDirectoryWithShared';
import { useQueryClient } from '@tanstack/react-query';
import { UploadCloudIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { FsDragDropProvider, FsDragOverlay } from '@/components/features/files/dnd';
import FilePageContextMenu from '@/components/features/files/FilePageContextMenu';
import UploadFSEntires from '@/components/features/files/forms/UploadFSEntires';
import FsFilters from '@/components/features/files/FsFilters';
import FilePageForms from '@/components/features/files/layouts/files-page/FilePageForms';
import FilesPageContent from '@/components/features/files/layouts/files-page/FilesPageContent';
import FsPageHeader from '@/components/features/files/layouts/header/FsPageHeader';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { useFileUpload } from '@/hooks/file-upload';
import { useFsData } from '@/hooks/fs';
import { useMediaQuery } from '@/hooks/media-query';
import { useFilesStorage } from '@/store/files-store';

export default function GroupFilesPage() {
  const { groupId: groupIdParam, id: directoryId } = useParams();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const breakpoint = useMediaQuery('md');

  // Files storage state
  const { resetSelected, setPermissions } = useFilesStorage();

  const [openFileUploadDrawer, setOpenFileUploadDrawer] = useState<boolean>(false);

  // determine if we are at /fs/group/:groupId/shared or /shared/:id
  const isSharedPath = useMemo<boolean>(() => location.pathname.includes('/shared'), [location.pathname]);

  const groupId = useMemo<number | undefined>(() => (groupIdParam ? Number(groupIdParam) : undefined), [groupIdParam]);

  const { data: group } = useGetGroup(groupId, groupId !== undefined);

  const useSharedIndex: boolean = isSharedPath && directoryId === undefined;
  // fetch directory or group shared listing
  const getDirectory = useGetGroupDirectoryWithShared({
    groupId,
    directoryId: useSharedIndex ? undefined : directoryId,
    useSharedEndpoint: useSharedIndex,
    enabled: groupId !== undefined,
  });

  const { dataModel, sorting, files, directories, totals } = useFsData(getDirectory.fsItems);

  // navigate between directories within group scope
  const groupDirectoryId = group?.directoryId;

  const handleNavigateTo = (nextDirectoryId?: string) => {
    if (groupId === undefined) {
      return;
    }

    if (isSharedPath) {
      if (nextDirectoryId) {
        navigate(`/fs/group/${groupId}/shared/${nextDirectoryId}`);
      } else {
        navigate(`/fs/group/${groupId}/shared`);
      }
      return;
    }

    if (nextDirectoryId && nextDirectoryId !== groupDirectoryId) {
      navigate(`/fs/group/${groupId}/dir/${nextDirectoryId}`);
      return;
    }

    if (groupDirectoryId) {
      navigate(`/fs/group/${groupId}/dir/${groupDirectoryId}`);
    } else {
      navigate(`/fs/group/${groupId}`);
    }
  };

  // switch between tabs - allow jumping to user FS or group shared
  const handleTabChange = (tab: string) => {
    if (groupId === undefined) {
      return;
    }

    if (tab === 'MY_DRIVE' && groupDirectoryId !== undefined) {
      navigate(`/fs/group/${groupId}/dir/${groupDirectoryId}`);
      return;
    }

    navigate(`/fs/group/${groupId}/shared`);
  };

  const filesForViewer = useMemo<FilePublicWithPermissions[]>(() => files.map((row) => row.original), [files]);

  const [fileUploadState, fileUploadActions] = useFileUpload({
    uploadFileFn: async (file, options) => {
      const response = await FileService.uploadFile({
        file: file,
        directoryId: options.directory?.id || directoryId || '',
        abortController: options.abortController,
        progressCallBack: options.progressCallback,
      });
      return response.data;
    },
    createDirectoryFn: async (name, config) => {
      const response = await DirectoryService.createDirectory({
        name: name,
        parentDirId: config?.id || directoryId || '',
      });
      return response.data;
    },
    postponeUpload: false,
    onFilesChange: (files) => {
      if (files.length > 0) {
        setOpenFileUploadDrawer(true);
      }
    },
    onFileUploaded: () => {
      queryClient.invalidateQueries({ queryKey: ['directory', directoryId] });
    },
  });

  const fileInputProps = fileUploadActions.createInputProps({
    multiple: true,
  });

  const directoryInputProps = fileUploadActions.createInputProps({
    webkitdirectory: 'true',
  } as InputHTMLAttributes<HTMLInputElement>);

  const handleUploadFiles = useCallback(() => {
    fileInputProps.openFileDialog();
    setTimeout(() => setOpenFileUploadDrawer(true), 100);
  }, [fileInputProps, setOpenFileUploadDrawer]);

  const handleUploadDir = useCallback(() => {
    directoryInputProps.openFileDialog();
    setTimeout(() => setOpenFileUploadDrawer(true), 100);
  }, [directoryInputProps, setOpenFileUploadDrawer]);

  const isContextMenuDisabled = useMemo<boolean>(() => {
    return getDirectory.isError || getDirectory.isLoading || !breakpoint || directoryId === undefined;
  }, [getDirectory.isError, getDirectory.isLoading, breakpoint, directoryId]);

  // Set permissions when directory changes (no directoryData for shared root)
  useEffect(() => {
    if (getDirectory.directoryData) {
      setPermissions(getDirectory.directoryData.permissions);
    }
  }, [getDirectory.directoryData, setPermissions]);

  // Reset selection when directory changes
  useEffect(() => {
    resetSelected();
  }, [directoryId, resetSelected]);

  return (
    <>
      <FilePageForms directoryId={directoryId ?? ''} files={filesForViewer} />
      <UploadFSEntires open={openFileUploadDrawer} onOpenChange={setOpenFileUploadDrawer} fileUploadState={fileUploadState} fileUploadActions={fileUploadActions} />
      <div className={'base-direction-col'} tabIndex={-1} {...fileUploadActions.createDropzoneProps().props}>
        {fileUploadState.isDragging && (
          <div
            className="bg-accent/75 pointer-events-none absolute top-0 right-0 bottom-0 left-0 z-50 flex flex-col items-center justify-center gap-2 backdrop-blur-xs select-none"
            aria-hidden="true"
            tabIndex={-1}
            role="presentation"
          >
            <FeaturedIcon size={'md'}>
              <UploadCloudIcon />
            </FeaturedIcon>
            <h4 className="text-foreground text-xl font-semibold">Upuść pliki tutaj</h4>
          </div>
        )}
        <FsPageHeader
          directory={getDirectory.directoryData}
          sorting={sorting}
          isLoading={getDirectory.isLoading}
          isError={getDirectory.isError}
          handleNavigateTo={handleNavigateTo}
          handleTabChange={handleTabChange}
          fsPageType={isSharedPath ? 'SHARED_DRIVE' : 'MY_DRIVE'}
          onUploadFilesOpen={handleUploadFiles}
          onUploadDirOpen={handleUploadDir}
          directoryId={directoryId}
        />
        <div className={'w-full'}>
          <FsFilters dataModel={dataModel} sorting={sorting} />
        </div>
        <FsDragDropProvider disabled={getDirectory.isPending} directoryId={directoryId ?? ''}>
          <div className={'h-full w-full'}>
            <FilePageContextMenu disabled={isContextMenuDisabled} onUploadFilesOpen={handleUploadFiles} onUploadDirOpen={handleUploadDir}>
              <FilesPageContent
                files={files}
                directories={directories}
                isEmpty={totals.all === 0}
                isSuccess={getDirectory.isSuccess}
                isLoading={getDirectory.isPending}
                isError={getDirectory.isError}
                directoryId={directoryId}
                errorMessage={getDirectory.errorMessage}
                handleNavigateTo={handleNavigateTo}
                onUploadFilesOpen={handleUploadFiles}
              />
            </FilePageContextMenu>
            <input {...fileInputProps.props} ref={fileInputProps.ref} className={'sr-only'} />
            <input {...directoryInputProps.props} ref={directoryInputProps.ref} className={'sr-only'} />
          </div>
          {/* Drag overlay for visual feedback */}
          <FsDragOverlay />
        </FsDragDropProvider>
      </div>
    </>
  );
}
